//src/services/deliveryService.js
import apiService from "./apiService"; // Import the reusable API service

const DELIVERY_API_URL = "/deliveries"; // Base endpoint for delivery operations


/**
 * Fetch today's delivery stats including total deliveries, collected amount, and due amount.
 * @returns {Object} - The response data containing today's stats.
 */
export const getTodayDeliveryStats = async () => {
  try {
    const response = await apiService.get(`${DELIVERY_API_URL}/today-stats`);
    return response.data; // Return the response data
  } catch (error) {
    console.error("Error fetching today's delivery stats:", error.message);
    throw error;
  }
};


/**
 * Fetch today's delivery stats by customer, including total deliveries, collected amount, and due amount.
 * @returns {Object} - The response data containing today's stats.
 */
export const getDeliverySummaryByCustomer = async () => {
  try {
    const response = await apiService.get(`${DELIVERY_API_URL}/deliveries/customer-summary`);
    return response.data; // Return the response data
  } catch (error) {
    console.error("Error fetching today's delivery stats:", error.message);
    throw error;
  }
};

/**
 * Create a new delivery.
 * @param {Object} deliveryData - The delivery data to be saved.
 * @returns {Object} - The response from the server.
 */
export const createDelivery = async (deliveryData) => {
  try {
    const response = await apiService.post(DELIVERY_API_URL, deliveryData);
    return response.data; // Return the response data
  } catch (error) {
    console.error("Error creating delivery:", error.message);
    throw error;
  }
};

/**
 * Fetch all deliveries with optional pagination.
 * @param {number} page - The page number to fetch.
 * @param {number} limit - The number of deliveries per page.
 * @returns {Object} - The response data containing deliveries and metadata.
 */
export const getAllDeliveries = async (page = 1, limit = 10) => {
  try {
    const response = await apiService.get(DELIVERY_API_URL, {
      params: { page, limit },
    });
    return response.data; // Return the response data
  } catch (error) {
    console.error("Error fetching deliveries:", error.message);
    throw error;
  }
};

/**
 * Fetch a specific delivery by ID.
 * @param {number} deliveryId - The ID of the delivery to fetch.
 * @returns {Object} - The delivery data.
 */
export const getDeliveryById = async (deliveryId) => {
  try {
    const response = await apiService.get(`${DELIVERY_API_URL}/${deliveryId}`);
    return response.data; // Return the response data
  } catch (error) {
    console.error("Error fetching delivery:", error.message);
    throw error;
  }
};

/**
 * Fetch a specific delivery by ID.
 * @param {number} deliveryId - The ID of the delivery to fetch.
 * @returns {Promise<Object>} - The delivery data.
 * @throws {Error} - Throws an error if the request fails.
 */
export const getDeliveriesByCustomerId = async (deliveryId) => {
  if (!deliveryId) {
    throw new Error("Delivery ID is required");
  }

  try {
    const response = await apiService.get(
      `${DELIVERY_API_URL}/customer/${deliveryId}`
    );
    if (response.data.success) {
      return response.data.data; // Return the delivery data on success
    } else {
      throw new Error(response.data.message || "Failed to fetch delivery");
    }
  } catch (error) {
    console.error("Error fetching delivery by ID:", error.message);
    throw error;
  }
};

/**
 * Update an existing delivery by ID.
 * @param {number} deliveryId - The ID of the delivery to update.
 * @param {Object} deliveryData - The updated delivery data.
 * @returns {Object} - The response from the server.
 */
export const updateDelivery = async (deliveryId, deliveryData) => {
  try {
    const response = await apiService.put(
      `${DELIVERY_API_URL}/${deliveryId}`,
      deliveryData
    );
    return response.data; // Return the response data
  } catch (error) {
    console.error("Error updating delivery:", error.message);
    throw error;
  }
};



/**
 * Create or update multiple loading/unloading records.
 * @param {Array} PayloadData - The records to be saved (loading/unloading details).
 * @returns {Object} - The response from the server.
 */
export const createMultipleLoadingUnloadingRecords = async (PayloadData) => {
  try {
    // Post request to the backend
    const response = await apiService.post(`${DELIVERY_API_URL}/loading-unloading`, {
      deliveries: PayloadData,
    });
    return response.data; // Return the response data
  } catch (error) {
    console.error("Error creating multiple loading/unloading records:", error.message);
    throw error;
  }
};


/**
 * Get loading/unloading records.
 * @returns {Object} - The response data containing records.
 */
export const getLoadingUnloadingRecords = async () => {
  try {
    const response = await apiService.get(`${DELIVERY_API_URL}/loading-unloading`);
    return response.data; // Return the response data containing the records
  } catch (error) {
    console.error("Error fetching loading/unloading records:", error.message);
    throw error;
  }
};

/**
 * Delete a delivery by ID.
 * @param {number} deliveryId - The ID of the delivery to delete.
 * @returns {Object} - The response from the server.
 */
export const deleteDelivery = async (deliveryId) => {
  try {
    const response = await apiService.delete(`${DELIVERY_API_URL}/${deliveryId}`);
    return response.data; // Return the response data
  } catch (error) {
    console.error("Error deleting delivery:", error.message);
    throw error;
  }
};